<script setup>
  import {computed, inject, onMounted, provide, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentCoreInfoDialog from "@components/Dialogs/CoreInfoDialog/CoreInfoDialog.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import MedicationComponentAthinaImportExport from "@pages/Records/Components/Sections/MedicationBar/Components/AthinaImportExport.vue";
  import MedicationComponentDetailDialog from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/DetailDialog.vue";
  import MedicationComponentIndicationDialog from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDialog.vue";
  import MedicationComponentPacSearch from "@pages/Records/Components/Sections/MedicationBar/Components/PacSearch.vue";
  import MedicationComponentPharmaceuticalServices from "@pages/Records/Components/Sections/MedicationBar/Components/PharmaceuticalServices.vue";
  import MedicationComponentPlanPrint from "@pages/Records/Components/Sections/MedicationBar/Components/PlanPrint.vue";
  import MedicationTable from "@pages/Records/Components/Sections/MedicationBar/Components/Table.vue";
  import {MedButton, MedInfoButton} from "@pharma4u/medicheck-components";
  import {delayWhileEditing} from "@utils/Helpers/DelayWhileEditing.js";
  import {cloneDeep, debounce} from "es-toolkit";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "RecordComponentSectionMedicationBar",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    changeIsEditable: {
      type: Function,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    problems: {
      type: Object,
      required: true,
    },
  });

  const page = usePage();

  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
  const getIsMassUpdateProcessing = inject("getIsMassUpdateProcessing");

  const refCoreInfoDialog = ref(null);
  const refEditDetailDialog = ref(null);
  const refIndicationDialog = ref(null);
  const refInvalidDosageConfirmationDialog = ref(null);
  const refMedicationBar = ref(null);

  const recordMedications = ref(cloneDeep(props.recordData.medications));
  const services = ref(null);
  const hasServices = ref(false);
  const athinaDialogIsOpen = ref(false);
  const mode = ref(null);

  const isMassUpdateProcessing = computed(() => getIsMassUpdateProcessing("medication"));

  onMounted(() => {
    loadServices();
  });

  watch(
    () => props.recordData.medications,
    (newValue, oldValue) => {
      recordMedications.value = cloneDeep(props.recordData.medications);

      // whenever a medication has added or deleted
      // we have to check for available services
      if (newValue?.length !== oldValue?.length) {
        loadServices();
      }
    },
    {deep: true},
  );

  const openDialog = (dialogName, data) => {
    switch (dialogName) {
      case "refCoreInfoDialog":
        refCoreInfoDialog.value.open(data);
        break;
      case "refEditDetailDialog":
        refEditDetailDialog.value.open(data);
        break;
      case "refIndicationDialog":
        refIndicationDialog.value.open(data);
        break;
    }
  };

  const loadServices = () => {
    useAuthHttp().get(
      route("api.records.pharmaceutical-service", {
        patient: page.props.patient.id,
        record: page.props.record.id,
      }),
      {
        onSuccess: (response) => {
          services.value = response;
          hasServices.value =
            services.value?.blood_pressure?.has ||
            services.value?.inhaler?.has ||
            services.value?.oral_antitumour_therapy?.has;
        },
      },
    );
  };

  const updateMedications = () => {
    setIsMassUpdateProcessing("medication", true);
    putMedications();
  };

  const putMedications = debounce(
    () => {
      delayWhileEditing(refMedicationBar, () => {
        router.patch(
          route("medications.mass-update", {
            patient: page.props.patient.id,
            record: page.props.record.id,
          }),
          {
            ...recordMedications.value,
          },
          {
            preserveScroll: true,
            only: ["flash", "errors", "medications"],
            onFinish: () => {
              setIsMassUpdateProcessing("medication", false);
            },
          },
        );
      });
    },
    500,
    {trailing: true},
  );

  const cancelUpdateMedications = () => {
    // cancel debounce
    putMedications.cancel();
    // cancel requests
    router.cancelAll();
    setIsMassUpdateProcessing("medication", false);
  };

  provide("medicationOpenDialog", openDialog);

  function checkInvalidDosageFirst(event) {
    if (props.problems.invalidDosageFields?.length > 0) {
      if (athinaDialogIsOpen.value) {
        // Athina Dialog is already open, so the error occurred while importing.
      } else {
        // Athina Dialog is not open, so intercept the button click.
        event.stopImmediatePropagation();
        refInvalidDosageConfirmationDialog.value.open();
      }
    }
  }

  const toggleMode = (newMode) => {
    mode.value = mode.value === newMode ? null : newMode;
  };
</script>

<template>
  <div class="flex items-center space-x-2 px-2 pb-4">
    <h5 class="text-2xl">Medikation</h5>

    <template v-if="!isEditable">
      <MedButton
        class="ml-auto"
        variant="primary"
        :data-test="getTestId('bar-set-editable-button')"
        @click="changeIsEditable"
      >
        Bearbeiten
      </MedButton>
    </template>

    <template v-if="isEditable">
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Fragen zur Medikation"
        :data-test="getTestId('medication-info-button')"
      >
        <ul class="list-disc space-y-1 pl-4 text-sm">
          <li>Welche AM wenden Sie (davon) aktuell an?</li>
          <li>Wie wenden Sie die AM an?</li>
          <li>Wofür oder wogegen wenden Sie die AM an?</li>
          <li>Wie sinnvoll/hilfreich finden Sie das/die AM?</li>
          <li>Haben Sie die Dosis (gelegentlich) geändert/ausgelassen?</li>
          <li>Welche weiteren AM wurden verordnet oder als Selbstmedikation angewendet?</li>
          <li>Wie häufig haben Sie im letzten Monat vergessen, Ihre AM einzunehmen?</li>
          <li>Haben Sie Probleme/Symptome, die Sie mit dem AM in Zusammenhang bringen?</li>
          <li>Wo/wie lagern Sie Ihre AM?</li>
        </ul>
      </MedInfoButton>

      <template v-if="(isEditable && recordMedications?.length >= 2) || mode === 'multi-select'">
        <MedButton
          class="ml-auto"
          :data-test="getTestId('toggle-multi-select-button')"
          @click="toggleMode('multi-select')"
        >
          <template v-if="mode === 'multi-select'">Abbrechen</template>
          <template v-else>Mehrfachauswahl</template>
        </MedButton>
      </template>
    </template>
  </div>

  <!-- Alert for not loadable medications -->
  <ComponentAlert
    v-if="problems.unloadableMedications?.length > 0 || problems.unloadableMedicationsSolved?.length > 0"
    type="warning"
    :test-id="getTestId('bar')"
  >
    <h4 class="font-bold">
      In diesem Bereich befinden sich Arzneimittel bzw. Medizinprodukte, die in der aktualisierten Datenbank ersetzt
      wurden.
    </h4>

    <p class="mt-2">
      Bitte tauschen Sie folgende Arzneimittel bzw. Medizinprodukte durch geeignete Neue aus, damit sie im Check erkannt
      und berücksichtigt werden:
    </p>

    <ul class="mt-2 ml-8 list-disc" :data-test="getTestId('bar-unloadable')">
      <template v-if="problems.unloadableMedications?.length > 0">
        <li v-for="(item, index) in problems.unloadableMedications" :key="'unsolved-' + index">
          <em>{{ item.fam }}</em>
        </li>
      </template>

      <template v-if="problems.unloadableMedicationsSolved?.length > 0">
        <!-- prettier-ignore -->
        <li v-for="(item, index) in problems.unloadableMedicationsSolved" :key="'solved-' + index">
          <em>{{ item.old.fam }}</em>
          (kann ggf. ersetzt werden durch <em>{{ item.new.Name }}</em>)
        </li>
      </template>
    </ul>
  </ComponentAlert>

  <!-- Table -->
  <section ref="refMedicationBar">
    <MedicationTable
      :record-medications="recordMedications"
      :is-editable="isEditable"
      :test-id="getTestId('bar')"
      :mode="mode"
      @update-medications="updateMedications"
      @cancel-update-medications="cancelUpdateMedications"
    />
  </section>

  <!-- Search -->
  <MedicationComponentPacSearch v-if="isEditable" :disabled="isMassUpdateProcessing" :test-id="getTestId('bar')" />

  <!-- Buttons -->
  <div v-if="$page.props.client.license.is_pro" class="flex justify-between py-2">
    <button @click.capture="checkInvalidDosageFirst($event)">
      <MedicationComponentAthinaImportExport
        :hide-import="!isEditable"
        :disabled="isMassUpdateProcessing"
        @is-open="
          (open) => {
            athinaDialogIsOpen = open;
          }
        "
      />
    </button>

    <template v-if="hasServices">
      <MedicationComponentPharmaceuticalServices :services="services" :test-id="getTestId('bar')" />
    </template>

    <button @click.capture="checkInvalidDosageFirst($event)">
      <MedicationComponentPlanPrint :disabled="isMassUpdateProcessing" reload-record />
    </button>
  </div>

  <!-- Dialogs -->
  <ComponentCoreInfoDialog ref="refCoreInfoDialog" />
  <MedicationComponentDetailDialog ref="refEditDetailDialog" :is-editable="isEditable" :test-id="getTestId('bar')" />
  <MedicationComponentIndicationDialog ref="refIndicationDialog" />

  <ComponentDialog
    ref="refInvalidDosageConfirmationDialog"
    dialog-title="Ungültige Medikation"
    dialog-content="Bitte korrigieren Sie die markierten Dosierungsangaben."
    dialog-type="information"
    primary-label="OK"
    :test-id="getTestId('bar-invalid-dosage')"
  />
</template>
